import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaStar } from "react-icons/fa";

import { apiConnector } from "../../services/apiconnector";
import { ratingsEndpoints } from "../../services/apis";
import { DS } from "../../utils/designSystem";

function ReviewSlider() {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiConnector("GET", ratingsEndpoints.REVIEWS_DETAILS_API);
        if (data?.success) setReviews(data.data ?? []);
      } catch (_) {}
      setLoaded(true);
    })();
  }, []);

  const count = reviews.length;

  // Clamp slidesToShow to actual count so react-slick never needs to clone
  const showDesktop = Math.min(count, 3);
  const showTablet  = Math.min(count, 2);

  const settings = {
    dots: count > 1,
    // Only enable infinite when there are MORE slides than are visible,
    // otherwise react-slick clones slides to fill the gap (causing duplicates)
    infinite: count > showDesktop,
    speed: 600,
    autoplay: count > 1,
    autoplaySpeed: 3000,
    cssEase: "ease-in-out",
    slidesToScroll: 1,
    arrows: count > showDesktop,
    responsive: [
      { breakpoint: 9999, settings: { slidesToShow: showDesktop } },
      { breakpoint: 1024, settings: { slidesToShow: showTablet } },
      { breakpoint: 640,  settings: { slidesToShow: 1, arrows: false, dots: count > 1 } },
    ],
  };

  const renderStars = (rating) =>
    Array(5)
      .fill(0)
      .map((_, i) => (
        <FaStar
          key={i}
          className={i < Math.round(rating) ? "text-yellow-50" : "text-richblack-600"}
        />
      ));

  if (!loaded) {
    return (
      <div className={`flex h-40 items-center justify-center ${DS.card.base}`}>
        <p className={DS.text.caption}>Loading reviews…</p>
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className={`flex h-40 items-center justify-center ${DS.card.base}`}>
        <p className={DS.text.caption}>No reviews yet — be the first!</p>
      </div>
    );
  }

  return (
    <Slider {...settings}>
      {reviews.map((review, i) => (
        <div key={i} className="px-2 pb-4 focus:outline-none">
          <div
            className={`flex h-[200px] flex-col justify-between ${DS.card.base} ${DS.card.hover} ${DS.card.pad} ${DS.shadow.card}`}
          >
            {/* User info */}
            <div className="flex items-center gap-3">
              <img
                src={
                  review?.user?.image ||
                  `https://api.dicebear.com/5.x/initials/svg?seed=${review?.user?.firstName} ${review?.user?.lastName}`
                }
                alt=""
                className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-richblack-600"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-richblack-5">
                  {review?.user?.firstName} {review?.user?.lastName}
                </p>
                <p className="truncate text-xs text-richblack-400">
                  {review?.course?.courseName}
                </p>
              </div>
            </div>

            {/* Review text */}
            <p className="mt-3 flex-1 overflow-hidden text-xs leading-relaxed text-richblack-300 line-clamp-3">
              {review?.review}
            </p>

            {/* Rating */}
            <div className="mt-3 flex items-center gap-1.5">
              <span className="text-sm font-bold text-yellow-50">
                {Number(review.rating).toFixed(1)}
              </span>
              <div className="flex gap-0.5 text-xs">{renderStars(review.rating)}</div>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  );
}

export default ReviewSlider;
